import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { MultiSelect } from '../ui/multi-select';
import { toast } from 'sonner';
import { AlertTriangle, Trash2 } from 'lucide-react';
import {
  CREATE_TASK_MUTATION,
  UPDATE_TASK_MUTATION,
  DELETE_TASK_MUTATION,
  MOVE_TASK_MUTATION,
  SET_TASK_ASSIGNEES_MUTATION,
  GET_PROJECT,
  GET_PROJECT_MEMBERS,
  GET_PROJECT_TASKS,
  MARK_TASK_SEEN_MUTATION,
} from '../../graphql/queries';
import {
  TASK_PRIORITY_LABELS,
  TASK_PRIORITY_OPTIONS,
  TaskPriority,
  compareEmployeesByLastName,
  toDateInputValue,
} from './projectUtils';
import { usePermission } from '../../hooks/usePermission';
import { AttachmentSection } from '../attachments/AttachmentSection';
import { TaskComments } from './TaskComments';

export interface TaskDialogTask {
  id: string;
  name: string;
  description?: string | null;
  priority: TaskPriority;
  startDate?: string | null;
  plannedEndDate?: string | null;
  columnId: string;
  assignees?: Array<{
    id: string;
    employeeId: string;
    employee?: { id: string; firstName: string; lastName: string } | null;
  }> | null;
}

interface TaskDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  /** Provided when editing an existing task. */
  task?: TaskDialogTask | null;
  /** Default column for newly-created tasks. Required in create mode. */
  defaultColumnId?: string | null;
  /** List of columns for the column picker (create mode). */
  columns?: Array<{ id: string; name: string }>;
  /** Current logged-in user id — used by the comments section. */
  currentUserId?: string;
  /** Whether current user can manage this project (admin/leader). */
  canManageProject?: boolean;
}

export const TaskDialog: React.FC<TaskDialogProps> = ({
  open,
  onClose,
  projectId,
  task,
  defaultColumnId,
  columns,
  currentUserId,
  canManageProject,
}) => {
  const isEdit = Boolean(task);
  const canCreate = usePermission('tasks.create');
  const canEdit = usePermission('tasks.edit');
  const canDelete = usePermission('tasks.delete');
  const canAssign = usePermission('tasks.assign');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('NORMAL');
  const [startDate, setStartDate] = useState('');
  const [plannedEndDate, setPlannedEndDate] = useState('');
  const [columnId, setColumnId] = useState<string>('');
  const [assignees, setAssignees] = useState<string[]>([]);
  const [errors, setErrors] = useState<{
    name?: string;
    dates?: string;
    submit?: string;
  }>({});
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  const { data: membersData, loading: membersLoading } = useQuery<{
    projectMembers: Array<{
      id: string;
      firstName: string;
      lastName: string;
      email: string;
    }>;
  }>(GET_PROJECT_MEMBERS, {
    variables: { projectId },
    skip: !open || !projectId,
    fetchPolicy: 'cache-and-network',
  });

  const refetchTasks = [
    { query: GET_PROJECT_TASKS, variables: { projectId } },
    { query: GET_PROJECT, variables: { id: projectId } },
  ];

  const [createTask, { loading: creating }] = useMutation(
    CREATE_TASK_MUTATION,
    {
      refetchQueries: refetchTasks,
      awaitRefetchQueries: false,
    },
  );
  const [updateTask, { loading: updating }] = useMutation(
    UPDATE_TASK_MUTATION,
    {
      refetchQueries: refetchTasks,
      awaitRefetchQueries: false,
    },
  );
  const [setTaskAssignees, { loading: settingAssignees }] = useMutation(
    SET_TASK_ASSIGNEES_MUTATION,
    {
      refetchQueries: refetchTasks,
      awaitRefetchQueries: false,
    },
  );
  const [moveTask, { loading: movingTask }] = useMutation(MOVE_TASK_MUTATION, {
    refetchQueries: refetchTasks,
    awaitRefetchQueries: false,
  });
  const [deleteTask, { loading: deleting }] = useMutation(
    DELETE_TASK_MUTATION,
    {
      refetchQueries: refetchTasks,
      awaitRefetchQueries: false,
    },
  );

  const saving = creating || updating || settingAssignees || movingTask;

  const [markTaskSeen] = useMutation(MARK_TASK_SEEN_MUTATION, {
    refetchQueries: [{ query: GET_PROJECT_TASKS, variables: { projectId } }],
    awaitRefetchQueries: false,
  });

  useEffect(() => {
    if (!open || !isEdit || !task) return;
    markTaskSeen({ variables: { taskId: task.id } }).catch(() => {});
  }, [open, isEdit, task, markTaskSeen]);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (task) {
      setName(task.name || '');
      setDescription(task.description || '');
      setPriority(task.priority || 'NORMAL');
      setStartDate(toDateInputValue(task.startDate));
      setPlannedEndDate(toDateInputValue(task.plannedEndDate));
      setColumnId(task.columnId || defaultColumnId || '');
      setAssignees(
        (task.assignees || [])
          .map((a) => a.employeeId)
          .filter(Boolean) as string[],
      );
    } else {
      setName('');
      setDescription('');
      setPriority('NORMAL');
      setStartDate('');
      setPlannedEndDate('');
      setColumnId(defaultColumnId || '');
      setAssignees([]);
    }
  }, [open, task, defaultColumnId]);

  const initial = useMemo(() => {
    if (task) {
      return {
        name: task.name || '',
        description: task.description || '',
        priority: task.priority || 'NORMAL',
        startDate: toDateInputValue(task.startDate),
        plannedEndDate: toDateInputValue(task.plannedEndDate),
        columnId: task.columnId || '',
        assignees: (task.assignees || [])
          .map((a) => a.employeeId)
          .filter(Boolean) as string[],
      };
    }
    return {
      name: '',
      description: '',
      priority: 'NORMAL' as TaskPriority,
      startDate: '',
      plannedEndDate: '',
      columnId: defaultColumnId || '',
      assignees: [] as string[],
    };
  }, [task, defaultColumnId]);

  const isDirty =
    name !== initial.name ||
    description !== initial.description ||
    priority !== initial.priority ||
    startDate !== initial.startDate ||
    plannedEndDate !== initial.plannedEndDate ||
    columnId !== initial.columnId ||
    assignees.length !== initial.assignees.length ||
    assignees.some((a) => !initial.assignees.includes(a));

  const memberOptions = useMemo(() => {
    return [...(membersData?.projectMembers || [])]
      .sort(compareEmployeesByLastName)
      .map((m) => ({
        value: m.id,
        label: `${m.lastName} ${m.firstName}`,
      }));
  }, [membersData]);

  const validate = (): boolean => {
    const errs: typeof errors = {};
    const trimmed = name.trim();
    if (trimmed.length < 1) {
      errs.name = 'Nazwa zadania jest wymagana';
    } else if (trimmed.length > 200) {
      errs.name = 'Nazwa nie może przekraczać 200 znaków';
    }
    if (startDate && plannedEndDate) {
      if (new Date(startDate) > new Date(plannedEndDate)) {
        errs.dates =
          'Planowana data zakończenia nie może być wcześniejsza niż data rozpoczęcia';
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const assigneesChanged =
    assignees.length !== initial.assignees.length ||
    assignees.some((a) => !initial.assignees.includes(a));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      const trimmedName = name.trim();
      if (isEdit && task) {
        if (!canEdit) {
          toast.error('Brak uprawnień do edycji zadań');
          return;
        }
        const updateInput: any = {
          name: trimmedName,
          description: description.trim() || null,
          priority,
          startDate: startDate || null,
          plannedEndDate: plannedEndDate || null,
        };
        await updateTask({
          variables: { id: task.id, input: updateInput },
        });

        if (columnId && columnId !== task.columnId) {
          await moveTask({
            variables: {
              input: {
                taskId: task.id,
                toColumnId: columnId,
                toIndex: 1_000_000,
              },
            },
          });
        }

        if (assigneesChanged) {
          if (!canAssign) {
            toast.error('Brak uprawnień do przypisywania osób do zadań');
          } else {
            await setTaskAssignees({
              variables: {
                input: { taskId: task.id, employeeIds: assignees },
              },
            });
          }
        }

        toast.success('Zadanie zaktualizowane');
      } else {
        if (!canCreate) {
          toast.error('Brak uprawnień do tworzenia zadań');
          return;
        }
        const createInput: any = {
          projectId,
          name: trimmedName,
          priority,
        };
        if (description.trim()) createInput.description = description.trim();
        if (startDate) createInput.startDate = startDate;
        if (plannedEndDate) createInput.plannedEndDate = plannedEndDate;
        if (columnId) createInput.columnId = columnId;
        if (assignees.length > 0) createInput.assigneeIds = assignees;

        await createTask({ variables: { input: createInput } });
        toast.success('Zadanie utworzone');
      }
      onClose();
    } catch (err: any) {
      const msg = err?.message || 'Nie udało się zapisać zadania';
      setErrors((prev) => ({ ...prev, submit: msg }));
      toast.error(msg);
    }
  };

  const handleDelete = async () => {
    if (!task) return;
    try {
      await deleteTask({ variables: { id: task.id } });
      toast.success('Zadanie usunięte');
      setConfirmDeleteOpen(false);
      onClose();
    } catch (err: any) {
      toast.error(err?.message || 'Nie udało się usunąć zadania');
    }
  };

  const handleClose = () => {
    if (saving || deleting) return;
    if (isDirty) {
      setConfirmDiscardOpen(true);
      return;
    }
    onClose();
  };

  const readOnly = isEdit ? !canEdit : !canCreate;

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!v) handleClose();
        }}
      >
        <DialogContent
          className={`${
            isEdit ? 'sm:max-w-[1100px]' : 'sm:max-w-[640px]'
          } max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden`}
        >
          <DialogHeader className="bg-background px-6 pt-6 pb-4 border-b">
            <DialogTitle>
              {isEdit ? `Edytuj zadanie: ${task?.name}` : 'Nowe zadanie'}
            </DialogTitle>
            <DialogDescription>
              {isEdit
                ? 'Zaktualizuj informacje o zadaniu'
                : 'Wypełnij formularz, aby dodać zadanie do projektu'}
            </DialogDescription>
          </DialogHeader>

          <div
            className={`flex-1 overflow-y-auto min-h-0 ${
              isEdit && task && currentUserId
                ? 'grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-0'
                : ''
            }`}
          >
            <div className="px-6 py-4">
            <form id="task-form" onSubmit={handleSubmit} className="space-y-4">
              {errors.submit && (
                <div className="text-red-600 text-sm bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                  {errors.submit}
                </div>
              )}

              <div>
                <Label htmlFor="task-name">
                  Nazwa <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="task-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="np. Przygotuj prezentację"
                  maxLength={200}
                  required
                  disabled={readOnly}
                />
                {errors.name && (
                  <p className="text-xs text-red-600 mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <Label htmlFor="task-description">Opis</Label>
                <Textarea
                  id="task-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Szczegóły zadania..."
                  rows={6}
                  maxLength={5000}
                  disabled={readOnly}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="task-priority">Priorytet</Label>
                  <Select
                    value={priority}
                    onValueChange={(v) => setPriority(v as TaskPriority)}
                    disabled={readOnly}
                  >
                    <SelectTrigger id="task-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TASK_PRIORITY_OPTIONS.map((p) => (
                        <SelectItem key={p} value={p}>
                          {TASK_PRIORITY_LABELS[p]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {columns && columns.length > 0 && (
                  <div>
                    <Label htmlFor="task-column">
                      {isEdit ? 'Status (kolumna)' : 'Kolumna'}
                    </Label>
                    <Select
                      value={columnId}
                      onValueChange={setColumnId}
                      disabled={readOnly}
                    >
                      <SelectTrigger id="task-column">
                        <SelectValue placeholder="Wybierz kolumnę" />
                      </SelectTrigger>
                      <SelectContent>
                        {columns.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="task-start">Data rozpoczęcia</Label>
                  <Input
                    id="task-start"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    disabled={readOnly}
                  />
                </div>
                <div>
                  <Label htmlFor="task-end">Planowana data zakończenia</Label>
                  <Input
                    id="task-end"
                    type="date"
                    value={plannedEndDate}
                    onChange={(e) => setPlannedEndDate(e.target.value)}
                    disabled={readOnly}
                  />
                </div>
              </div>
              {errors.dates && (
                <p className="text-xs text-red-600">{errors.dates}</p>
              )}

              <div>
                <Label>Przypisane osoby</Label>
                <MultiSelect
                  options={memberOptions}
                  selected={assignees}
                  onChange={setAssignees}
                  placeholder={
                    membersLoading
                      ? 'Ładowanie...'
                      : memberOptions.length === 0
                      ? 'Brak członków projektu'
                      : 'Wybierz osoby'
                  }
                  searchable
                  searchPlaceholder="Szukaj po imieniu/nazwisku..."
                />
                {!canAssign && isEdit && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Brak uprawnień do przypisywania osób — zmiany w tym polu
                    nie zostaną zapisane.
                  </p>
                )}
              </div>

              {isEdit && task && (
                <div className="border-t pt-4">
                  <AttachmentSection
                    entityType="TASK"
                    entityId={task.id}
                    canUpload={canEdit || canAssign}
                    canDelete={canEdit}
                  />
                </div>
              )}
            </form>
            </div>

            {isEdit && task && currentUserId && (
              <div className="px-6 py-4 border-l border-border bg-muted/20">
                <TaskComments
                  taskId={task.id}
                  projectId={projectId}
                  currentUserId={currentUserId}
                  members={(membersData?.projectMembers || []).map((m) => ({
                    id: m.id,
                    firstName: m.firstName,
                    lastName: m.lastName,
                  }))}
                  canManageProject={canManageProject}
                />
              </div>
            )}
          </div>

          <DialogFooter className="bg-background px-6 pb-6 pt-4 border-t flex-row sm:justify-between">
            <div>
              {isEdit && canDelete && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setConfirmDeleteOpen(true)}
                  disabled={saving || deleting}
                  className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Usuń zadanie
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={saving || deleting}
              >
                Anuluj
              </Button>
              {!readOnly && (
                <Button type="submit" form="task-form" disabled={saving}>
                  {saving
                    ? 'Zapisywanie...'
                    : isEdit
                    ? 'Zapisz zmiany'
                    : 'Utwórz zadanie'}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={confirmDeleteOpen}
        onOpenChange={(v) => {
          if (!v) setConfirmDeleteOpen(false);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Usuń zadanie
            </DialogTitle>
            <DialogDescription>
              Czy na pewno chcesz usunąć zadanie <strong>{task?.name}</strong>?
              Tej operacji nie można cofnąć.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDeleteOpen(false)}
              disabled={deleting}
            >
              Anuluj
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Usuwanie...' : 'Usuń'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discard confirmation */}
      <Dialog
        open={confirmDiscardOpen}
        onOpenChange={(v) => {
          if (!v) setConfirmDiscardOpen(false);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Niezapisane zmiany
            </DialogTitle>
            <DialogDescription>
              Masz niezapisane zmiany. Czy na pewno chcesz zamknąć okno bez
              zapisywania?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDiscardOpen(false)}
            >
              Wróć do edycji
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmDiscardOpen(false);
                onClose();
              }}
            >
              Odrzuć zmiany
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
